import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DialogWithForm } from '@strata/react-dialog';
import { Hyperlink } from '@strata/react-hyperlink';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { postHealthPlanNotesAttestationForReferral } from 'api/clientAPI';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';

const ValidationSchema = Yup.object({
    agree: Yup.array(Yup.string().required())
        .required(validationStrings.requiredField.errorMessage)
        .min(1, validationStrings.requiredField.errorMessage),
});

interface HealthPlanNotesAgreeDialogProps {
    isOpen: boolean;
    onSecondaryButtonClick: () => void;
    onSubmitSuccess: () => void;
    referralId?: string;
    payerId?: string;
}

export const HealthPlanNotesAgreeDialog = ({
    isOpen,
    onSecondaryButtonClick,
    onSubmitSuccess,
    referralId = undefined,
    payerId = undefined,
}: HealthPlanNotesAgreeDialogProps) => {
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);
    return (
        <Formik
            initialValues={{ agree: [] } as Yup.InferType<typeof ValidationSchema>}
            enableReinitialize
            validationSchema={ValidationSchema}
            onSubmit={async (_values, { resetForm }) => {
                try {
                    if (payerId && referralId) {
                        await postHealthPlanNotesAttestationForReferral({
                            npi: selectedNpi!,
                            referralId,
                            payerId,
                        });
                        onSubmitSuccess();
                        resetForm();
                    }
                } catch (err) {
                    handleError('Error submitting health plan notes attestation.', err as Error);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Highmark Wholecare Network Essentials"
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        formik.resetForm();
                        onSecondaryButtonClick();
                    }}
                    primaryButtonLabel="Save"
                    isPrimaryButtonDisabled={!(formik.isValid && formik.dirty)}
                    dialogContent={
                        <>
                            <div className="margin__bottom--large">
                                You agree to the provider obligations related to servicing a
                                Specialty medication for a Highmark Wholecare member:
                            </div>
                            <CheckboxListFormik
                                name="agree"
                                required
                                options={[
                                    {
                                        value: 'agree',
                                        component: (
                                            <span>
                                                {'I agree to the '}
                                                <Hyperlink
                                                    linkText="provider requirements"
                                                    navUrl={AppRoutes.healthPlanNotes}
                                                />
                                                {'. '}
                                            </span>
                                        ),
                                    },
                                ]}
                                verticalSpacingSmall
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
